import Analytics from "analytics";
import { EventName, Xapi } from "./types/xapi";
import { TrackResp } from "./types/type";
import { store } from "../../../store/store";
import { telemetryApi } from "./xapi";

const umamiForwardEnabled =
  (import.meta.env.VITE_UMAMI_FORWARD_EVENTS ?? "true") !== "false";

type UmamiEventPayload = Record<string, string | number | boolean>;

type UmamiEvent = {
  event: string;
  data: UmamiEventPayload;
};

const pendingUmamiEvents: UmamiEvent[] = [];
let umamiFlushTimer: number | undefined;
let umamiFlushAttempts = 0;

const MAX_UMAMI_QUEUE_SIZE = 20;
const MAX_UMAMI_FLUSH_ATTEMPTS = 40;
const UMAMI_SEARCH_EVENT = "LXP_SEARCH";
const UMAMI_SEARCH_OBJECT_ID = "catalog_search";
const UMAMI_SEARCH_OBJECT_TYPE = "CATALOG_SEARCH";
const UMAMI_SEARCH_VERB = "SEARCHED";

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: UmamiEventPayload) => void;
    };
  }
}

function sanitizeSearchQuery(value: string) {
  return value
    .trim()
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email]")
    .replace(
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
      "[id]"
    )
    .replace(/\b\d{6,}\b/g, "[number]")
    .replace(/\s+/g, " ")
    .slice(0, 100);
}

function getLengthBucket(value: string) {
  if (value.length < 10) return "short";
  if (value.length < 40) return "medium";
  return "long";
}

function getUmamiEventName(objectType: string, verb: string) {
  return `${objectType}_${verb}`.toUpperCase().replace(/[^A-Z0-9_]/g, "_");
}

function addOptionalUmamiField(
  data: UmamiEventPayload,
  key: string,
  value?: string | number | boolean
) {
  if (value === undefined || value === null || value === "") return;
  data[key] = value;
}

function flushPendingUmamiEvents() {
  umamiFlushAttempts += 1;

  if (typeof window !== "undefined" && window.umami) {
    while (pendingUmamiEvents.length > 0) {
      const item = pendingUmamiEvents.shift();
      if (item) {
        window.umami.track(item.event, item.data);
      }
    }
  }

  if (
    pendingUmamiEvents.length === 0 ||
    umamiFlushAttempts >= MAX_UMAMI_FLUSH_ATTEMPTS
  ) {
    if (umamiFlushTimer) {
      window.clearInterval(umamiFlushTimer);
      umamiFlushTimer = undefined;
    }
    umamiFlushAttempts = 0;
  }
}

function trackUmamiEvent(event: string, data: UmamiEventPayload) {
  if (!umamiForwardEnabled || typeof window === "undefined") return;
  // Respect the browser's Do Not Track header for all custom events too,
  // consistent with data-do-not-track on the Umami script tag.
  if (navigator.doNotTrack === "1") return;

  if (window.umami) {
    window.umami.track(event, data);
    return;
  }

  pendingUmamiEvents.push({ event, data });
  if (pendingUmamiEvents.length > MAX_UMAMI_QUEUE_SIZE) {
    pendingUmamiEvents.shift();
  }

  if (!umamiFlushTimer) {
    umamiFlushTimer = window.setInterval(flushPendingUmamiEvents, 250);
  }
}

function trackUmamiXapiEvent(data: TrackResp) {
  if (!umamiForwardEnabled) return;
  if (typeof window === "undefined") return;

  const xapiStatement = data.payload.properties;
  const verb = xapiStatement.verb?.id;
  const objectType = xapiStatement.object?.objectType;
  const objectId = xapiStatement.object?.id;

  if (!verb || !objectType || !objectId) return;

  const eventPayload: UmamiEventPayload = {
    object_type: objectType,
    object_id: objectId,
    verb,
    handler: data.payload.event
  };

  addOptionalUmamiField(
    eventPayload,
    "parent_id",
    xapiStatement.object?.extensions?.accessedFrom
  );
  addOptionalUmamiField(
    eventPayload,
    "parent_type",
    xapiStatement.object?.extensions?.parentType
  );

  trackUmamiEvent(getUmamiEventName(objectType, verb), eventPayload);
}

export function trackUmamiLxpSearch({
  query,
  source,
  smartSearch
}: {
  query: string;
  source: string;
  smartSearch?: boolean | string;
}) {
  const trimmedQuery = query.trim();
  const sanitizedQuery = sanitizeSearchQuery(trimmedQuery);

  if (!sanitizedQuery) return;

  trackUmamiEvent(UMAMI_SEARCH_EVENT, {
    object_type: UMAMI_SEARCH_OBJECT_TYPE,
    object_id: UMAMI_SEARCH_OBJECT_ID,
    verb: UMAMI_SEARCH_VERB,
    query: sanitizedQuery,
    source,
    smart_search: smartSearch === true || smartSearch === "true",
    length_bucket: getLengthBucket(trimmedQuery),
    redacted: sanitizedQuery !== trimmedQuery.slice(0, 100)
  });
}

export const analytics = Analytics({
  app: "gl-muse",
  plugins: [
    {
      name: "gl-muse-analytics",
      page: (data: TrackResp) => {
        const xapiStatement = data.payload.properties;
        store.dispatch(
          telemetryApi.endpoints.addTelemetry.initiate(xapiStatement)
        );
      },
      track: (data: TrackResp): void => {
        const xapiStatement = data.payload.properties;
        store.dispatch(
          telemetryApi.endpoints.addTelemetry.initiate(xapiStatement)
        );
      }
    },
    {
      name: "gl-muse-umami",
      // Allowlist projection only. Never forward actor / context / free-text
      // fields — those carry PII and would break Umami's GDPR posture.
      track: trackUmamiXapiEvent
    }
  ]
});

export function trackEvent<T extends object>(
  eventName: EventName,
  data: Xapi,
  options?: unknown,
  callback?: (...params: unknown[]) => unknown
) {
  analytics.track(eventName, data, options, callback);
}
